/**
 * User Register Handler
 * Event handler implementation only
 */

import { notifier } from "../../core/notifier/notifier";
import { formValidator } from "../../util/form-validator";
import { UserRegisterApi } from "./user-register-api";
import type * as z from "zod";
import { CreateUserPreRegistration201Response } from "../../api/generated/user_registers.zod";
import type { UserRegisterForm } from "./user-register-form";

type UserRegisterApiResponse = z.infer<typeof CreateUserPreRegistration201Response>;

export class UserRegisterHandler {
    public form: UserRegisterForm;

    public constructor(form: UserRegisterForm) {
        this.form = form;

        this.handleSubmit = this.handleSubmit.bind(this);
    }

    public async handleSubmit(event: SubmitEvent): Promise<void> {
        const validation = formValidator.validate(this.form.form, { showNotification: true });
        if (!validation.isValid) {
            event.preventDefault();
            return;
        }

        event.preventDefault();

        const payload = this.form.getPayload();

        this.form.setSubmitting(true);

        try {
            const response = await UserRegisterApi.createPreRegistration(payload);

            if (!response.ok) {
                await notifier.fromResponse(response, "registration_request_failed");
                return;
            }

            const data = (await response.json().catch(() => ({}))) as UserRegisterApiResponse;
            if (data && data.success) {
                notifier.success("registration_email_sent");
                this.form.clearPassword();
                return;
            }

            notifier.error("registration_request_failed");
        } catch (error) {
            console.error("[UserRegister] submit failed", error);
            notifier.error("registration_request_failed");
        } finally {
            this.form.setSubmitting(false);
        }
    }
}
